import {Trans, t} from '@lingui/macro'
import type {ReactNode} from 'react'
import styled from 'styled-components'

import {Inline, tokens, Text} from '@pleo-io/telescope'
import {CashSingle} from '@pleo-io/telescope-icons'
import type {SpacingValues} from '@pleo-io/telescope-tokens/dist/spacing.types'

import {BillingInterval, Interval} from '~/api/types/billing'
import type {PlanType, PlanUpgradeSource} from '~/api/types/plan'
import {dayjs} from '~/dates/dayjs'
import AdditionalUserPricing from '~/paywall/manage-plan/additional-user-pricing'
import {choosePlanModalActioned} from '~/tracking'

import type {
    UpdateSubscriptionRequestPlanUpgradeSource,
    UpdateSubscriptionRequest,
    AmountCurrency,
} from '../../../bff/moons/generated/hydra'
import type {PageOrigin, PageSection} from '../empty-states/empty-states.helper'
import type {PlansInfo} from '../index.bff'

export type AllowedPlanType = 'STARTER' | 'ESSENTIAL' | 'ADVANCED'
export type PlanColumnType = 'SIMPLE' | 'AWESOME'
export type BillingCommitment = 'FLEXIBLE' | 'MONTHLY' | 'YEARLY'
export type PlanPrices = PlansInfo['plansPrices'][AllowedPlanType]

const getRealTimeExpenseTrackingConf = () => ({
    title: t`Real-time expense tracking`,
})

const get3CompanyCardsConf = () => ({
    title: t`3 company cards`,
})

const getCardExpensesOnlyConf = () => ({
    title: t`Card expenses only`,
})

const getAnalyticsDashboardConf = () => ({
    title: t`Analytics dashboard`,
    tooltip: t`Discover top spenders and spend categories, receipt capture rates, and more.`,
})

const getSpendingLimitsConf = () => ({
    title: t`Spending limits`,
    tooltip: t`Set limits for card holders based on their role or seniority.`,
})

const getSubscriptionManagementConf = () => ({
    title: t`Subscription management`,
    tooltip: t`Track recurring payments and stop paying for subscriptions you don't need.`,
})

const GetApprovalWorkflowsConf = () => ({
    title: t`Approval workflows`,
    tooltip: t`Get managers to review expenses above a certain amount. `,
})

const getIndividualCardsConf = () => ({
    title: t`Individual cards`,
    tooltip: t`Trace every expense to a specific employee or cardholder.`,
})

const getReimbursementsConf = () => ({
    title: t`Reimbursements`,
    tooltip: t`Reimburse employees for out-of-pocket expenses and mileage.`,
})

const getOutOfPocketExpensesConf = () => ({
    title: t`Out-of-pocket expenses`,
    tooltip: t`Reimburse employees for out-of-pocket expenses and mileage.`,
})

const getInvoicePaymentConf = () => ({
    title: t`Invoice payments`,
    tooltip: t`Pay up to 20 invoices a month through Pleo with no transfer fees.`,
})

const getApprovalWorkflowsConf = () => ({
    title: t`Approval workflows`,
    tooltip: t`Get managers to review expenses above a certain amount.`,
})

const getMultiStepApprovalWorkflowsConf = () => ({
    title: t`Multi-step approval workflows`,
    tooltip: t`Get managers to review expenses above a certain amount.`,
})

const getTeamAnalyticsConf = () => ({
    title: t`Team analytics`,
    tooltip: t`Discover top spenders and spend categories, receipt capture rates, and more.`,
})

const getBudgetsConf = () => ({
    title: t`Budgets`,
    tooltip: t`Track spending in relation to department or client budgets.`,
})

const getOpenAPIConf = () => ({
    title: t`Open API`,
    tooltip: t`Leverage your Pleo data and build your own custom solutions.`,
})

const getSingleSignOnConf = () => ({
    title: t`Single Sign-On`,
    tooltip: t`Make it easy for employees to log in securely with your chosen SSO provider.`,
})

const getCustomerSuccessManagerConf = () => ({
    title: t`Customer Success Manager`,
    tooltip: t`A real person at Pleo to help with questions, onboarding, and best practices.`,
})

const getBenefitCashbackConf = ({
    isAdvanced,
    isCurrent,
}: {
    isAdvanced?: boolean
    isCurrent?: boolean
}) => ({
    title: (
        <Inline justifyContent="start" alignItems="center" space={6}>
            <CashSingle size={16} color={isCurrent ? tokens.shade600 : tokens.green700} />
            {isAdvanced ? <Trans>1% cashback</Trans> : <Trans>0.5% cashback</Trans>}
        </Inline>
    ),
    tooltip: t`Earn back up to 100% of your Pleo subscription costs if you commit to a year.`,
})

const getStarterBenefitList = ({
    priceAtmWithdrawal,
    priceExtBookeeper,
}: {
    priceAtmWithdrawal: string
    priceExtBookeeper: string
}) => [
    {title: t`No cashback`},
    {
        title: t`${priceAtmWithdrawal} / ATM Withdrawal`,
    },
    {title: t`2.49% Foreign exchange fee`},
    {
        title: t`${priceExtBookeeper} / External Bookkeeper`,
    },
]

const getEssentialOrAdvancedBenefitListWithStarter = ({
    priceExtBookeeper,
    isAdvanced,
}: {
    priceExtBookeeper: string
    isAdvanced?: boolean
}) => [
    getBenefitCashbackConf({isAdvanced}),
    {title: t`Unlimited ATM withdrawals`},
    {title: t`1.99% Foreign exchange fee`},
    {title: t`${priceExtBookeeper} / External Bookkeeper`},
]

export const getEssentialBenefitList = ({isCurrent}: {isCurrent?: boolean}) => [
    getBenefitCashbackConf({isCurrent}),
    {title: t`1 or 2 entities`},
    {title: t`20 invoice payments`},
]

const getAdvancedBenefitList = () => [
    getBenefitCashbackConf({isAdvanced: true}),
    {title: t`Unlimited entities`},
    {title: t`100 invoice payments`},
]

export const getPlanUpdateModalConf = ({pageOrigin}: {pageOrigin: PageOrigin}) => {
    switch (pageOrigin) {
        case 'analytics':
            return {
                title: t`You're seconds away from smarter spending`,
            }
        case 'subscriptions':
            return {
                title: t`You're seconds away from smarter spending`,
            }
        case 'limits':
            return {
                title: t`Lead the way to smarter spending`,
            }
        case 'pocket':
            return {
                title: t`A spend solution your team can count on`,
            }
        case 'budgets':
            return {
                title: t`Create your ideal spend solution`,
            }
        case 'teams':
        case 'company-review':
            return {
                title: t`Lead the way to smarter spending`,
            }
        default:
            return {
                title: '',
            }
    }
}

interface PlanUpdateOptions {
    description: string
    featuresLabel: string
    featuresList: Array<{title: ReactNode; tooltip?: string}>
    benefitsList: Array<{title: ReactNode; tooltip?: string}>
}

export const getPlanUpdatePagesConf = ({
    pageOrigin,
    priceAtmWithdrawal,
    priceExtBookeeperStarter,
    priceExtBookeeperEssentialAndAdvanced,
}: {
    pageOrigin: PageOrigin
    priceAtmWithdrawal: string
    priceExtBookeeperStarter: string
    priceExtBookeeperEssentialAndAdvanced: string
}): {
    title: string
    pillLabel: string
    plansConf: Record<AllowedPlanType, Record<PlanColumnType, PlanUpdateOptions>>
} => {
    switch (pageOrigin) {
        case 'analytics':
            return {
                title: t`You're seconds away from smarter spending`,
                pillLabel: t`Includes Analytics`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend management`,
                            featuresList: [getRealTimeExpenseTrackingConf()],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: t`For teams who want to keep a closer eye on company spending`,
                            featuresLabel: t`Greater visibility and control`,
                            featuresList: [
                                getAnalyticsDashboardConf(),
                                getSpendingLimitsConf(),
                                getSubscriptionManagementConf(),
                                GetApprovalWorkflowsConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                            }),
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                },
            }
        case 'subscriptions':
            return {
                title: t`You're seconds away from smarter spending`,
                pillLabel: t`Includes Subscriptions`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend overview`,
                            featuresList: [
                                get3CompanyCardsConf(),
                                getRealTimeExpenseTrackingConf(),
                            ],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: t`For teams who want to keep a closer eye on company spending`,
                            featuresLabel: t`Greater visibility and control`,
                            featuresList: [
                                getIndividualCardsConf(),
                                getSubscriptionManagementConf(),
                                GetApprovalWorkflowsConf(),
                                getAnalyticsDashboardConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                            }),
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                },
            }
        case 'limits':
            return {
                title: t`Lead the way to smarter spending`,
                pillLabel: t`Includes Limits`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend overview`,
                            featuresList: [getRealTimeExpenseTrackingConf()],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: t`For teams looking to improve cost-effectiveness and prevent overspending`,
                            featuresLabel: t`Greater visibility and control`,
                            featuresList: [
                                getSpendingLimitsConf(),
                                GetApprovalWorkflowsConf(),
                                getReimbursementsConf(),
                                getAnalyticsDashboardConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                            }),
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                },
            }
        case 'pocket':
            return {
                title: t`A spend solution your team can count on`,
                pillLabel: t`Includes Reimbursements`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend overview`,
                            featuresList: [getCardExpensesOnlyConf()],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: t`For teams that move fast and need spend management to follow suit`,
                            featuresLabel: t`More flexibility for the finance team`,
                            featuresList: [
                                getOutOfPocketExpensesConf(),
                                getInvoicePaymentConf(),
                                getSubscriptionManagementConf(),
                                getAnalyticsDashboardConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                            }),
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                },
            }
        case 'budgets':
            return {
                title: t`Create your ideal spend solution`,
                pillLabel: t`Includes Budgets`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend overview`,
                            featuresList: [getRealTimeExpenseTrackingConf()],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: t`For companies on a steady path to smarter business spending`,
                            featuresLabel: t`Centralise business spend`,
                            featuresList: [getRealTimeExpenseTrackingConf()],
                            benefitsList: getEssentialBenefitList({isCurrent: true}),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: t`For larger organisations who need spend management at scale`,
                            featuresLabel: t`Complete control for the finance team`,
                            featuresList: [
                                getBudgetsConf(),
                                getSpendingLimitsConf(),
                                getApprovalWorkflowsConf(),
                                getAnalyticsDashboardConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                                isAdvanced: true,
                            }),
                        },
                        AWESOME: {
                            description: t`For larger organisations who need spend management at scale`,
                            featuresLabel: t`Complete control for the finance team`,
                            featuresList: [
                                getBudgetsConf(),
                                getOpenAPIConf(),
                                getSingleSignOnConf(),
                                getCustomerSuccessManagerConf(),
                            ],
                            benefitsList: getAdvancedBenefitList(),
                        },
                    },
                },
            }
        case 'company-review':
            return {
                title: t`Lead the way to smarter spending`,
                pillLabel: t`Includes Company Review`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend overview`,
                            featuresList: [getRealTimeExpenseTrackingConf()],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: t`For teams looking to improve cost-effectiveness and prevent overspending`,
                            featuresLabel: t`Added customisation and control`,
                            featuresList: [
                                getMultiStepApprovalWorkflowsConf(),
                                getTeamAnalyticsConf(),
                                getSpendingLimitsConf(),
                                getIndividualCardsConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                            }),
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                },
            }
        case 'teams':
        default:
            return {
                title: t`Lead the way to smarter spending`,
                pillLabel: t`Includes Teams`,
                plansConf: {
                    STARTER: {
                        SIMPLE: {
                            description: t`For startups and companies who are just beginning their Pleo journey`,
                            featuresLabel: t`Basic spend overview`,
                            featuresList: [getRealTimeExpenseTrackingConf()],
                            benefitsList: getStarterBenefitList({
                                priceAtmWithdrawal,
                                priceExtBookeeper: priceExtBookeeperStarter,
                            }),
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                    ESSENTIAL: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: t`For teams looking to improve cost-effectiveness and prevent overspending`,
                            featuresLabel: t`Added customisation and control`,
                            featuresList: [
                                getApprovalWorkflowsConf(),
                                getTeamAnalyticsConf(),
                                getSpendingLimitsConf(),
                                getIndividualCardsConf(),
                            ],
                            benefitsList: getEssentialOrAdvancedBenefitListWithStarter({
                                priceExtBookeeper: priceExtBookeeperEssentialAndAdvanced,
                            }),
                        },
                    },
                    ADVANCED: {
                        SIMPLE: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                        AWESOME: {
                            description: '',
                            featuresLabel: '',
                            featuresList: [],
                            benefitsList: [],
                        },
                    },
                },
            }
    }
}

export const SmallText = styled(Text).attrs({variant: 'small-subtle'})`
    color: ${tokens.shade800};
`

export const DashedText = styled(SmallText)`
    text-decoration: underline dotted ${tokens.shade800};
    cursor: pointer;
`

const usersIncludedMobileNode = (
    <Trans>
        3 users included (<SmallText>add more for a fee</SmallText>)
    </Trans>
)

const addMoreForAFeeNode = (
    <DashedText>
        <Trans>add more for a fee</Trans>
    </DashedText>
)

const getUsersIncludedDesktopNode = ({
    planType,
    planInterval,
    planCurrency,
    billingInterval,
}: {
    planType: AllowedPlanType
    planInterval: Interval
    planCurrency: string
    billingInterval: BillingInterval
}): ReactNode => (
    <>
        <Trans>
            3 users included (
            <AdditionalUserPricing
                planType={planType as PlanType}
                planInterval={planInterval}
                planCurrency={planCurrency}
                billingInterval={billingInterval}
                alternativeLabel={addMoreForAFeeNode}
            />
            )
        </Trans>
    </>
)

const getTodayPriceText = ({priceText}: {priceText: string}) => (
    <Trans>
        Today you'll pay <b>{priceText}</b> (incl. VAT) from your Pleo Wallet
    </Trans>
)

export const dateFormat = 'D MMMM YYYY'

export const getBannerContentConf = ({
    priceText,
    planType,
    planCurrency,
    isMobile,
}: {
    priceText: string
    planType: AllowedPlanType
    planCurrency: string
    isMobile: boolean
}): Record<BillingCommitment, Array<{node: ReactNode; gap?: 0 | SpacingValues}>> => {
    const aYearFromToday = dayjs().add(1, 'year').format(dateFormat)
    const aMonthFromToday = dayjs().add(1, 'month').format(dateFormat)

    return {
        YEARLY: [
            {
                node: getTodayPriceText({priceText}),
            },
            {
                node: (
                    <Trans>
                        Renews next year: <b>{aYearFromToday}</b>
                    </Trans>
                ),
            },
            {
                node: isMobile
                    ? usersIncludedMobileNode
                    : getUsersIncludedDesktopNode({
                          planType,
                          planInterval: Interval.YEAR,
                          planCurrency,
                          billingInterval: BillingInterval.YEARLY,
                      }),
                gap: 0,
            },
        ],
        MONTHLY: [
            {
                node: getTodayPriceText({priceText}),
            },
            {node: <Trans>11 additional monthly payments</Trans>},
            {
                node: isMobile
                    ? usersIncludedMobileNode
                    : getUsersIncludedDesktopNode({
                          planType,
                          planInterval: Interval.YEAR,
                          planCurrency,
                          billingInterval: BillingInterval.MONTHLY,
                      }),
                gap: 0,
            },
        ],
        FLEXIBLE: [
            {
                node: getTodayPriceText({priceText}),
            },
            {
                node: (
                    <Trans>
                        Renews next month: <b>{aMonthFromToday}</b>
                    </Trans>
                ),
            },
            {
                node: isMobile
                    ? usersIncludedMobileNode
                    : getUsersIncludedDesktopNode({
                          planType,
                          planInterval: Interval.MONTH,
                          planCurrency,
                          billingInterval: BillingInterval.MONTHLY,
                      }),
                gap: 0,
            },
        ],
    }
}

export interface BillingOptionConf {
    label: ReactNode
    cashback: ReactNode
    subLabel: ReactNode
    discountedFromPrice: string
    fromPrice: string
}

export const getPlanBillingConf = ({
    cashbackPercentage,
    priceYearlySum,
    priceMonthly,
    priceDiscountedMonthly,
}: {
    cashbackPercentage: string
    priceYearlySum: string
    priceMonthly: string
    priceDiscountedMonthly: string
}): Record<BillingCommitment, BillingOptionConf> => ({
    YEARLY: {
        label: <Trans>Yearly</Trans>,
        cashback: <Trans>Cashback {cashbackPercentage}</Trans>,
        subLabel: <Trans>One payment of {priceYearlySum}</Trans>,
        discountedFromPrice: priceMonthly,
        fromPrice: priceDiscountedMonthly,
    },
    MONTHLY: {
        label: <Trans>Monthly (12 months)</Trans>,
        cashback: <Trans>Cashback {cashbackPercentage}</Trans>,
        subLabel: <Trans>Twelve payments of {priceMonthly}</Trans>,
        discountedFromPrice: '',
        fromPrice: priceMonthly,
    },
    FLEXIBLE: {
        label: <Trans>Monthly (flexible)</Trans>,
        cashback: <Trans>No cashback</Trans>,
        subLabel: <Trans>Cancel anytime</Trans>,
        discountedFromPrice: '',
        fromPrice: priceMonthly,
    },
})

export const starterAddonsPricesPerCurrencyMap: Record<
    'ATM_WITHDRAWALS' | 'EXTERNAL_BOOKKEEPER',
    Record<string, string>
> = {
    ATM_WITHDRAWALS: {
        DKK: '45 DKK',
        SEK: '60 SEK',
        GBP: '5 GBP',
        NOK: '52 NOK',
        EUR: '5 EUR',
    },
    EXTERNAL_BOOKKEEPER: {
        DKK: '80 DKK',
        SEK: '110 SEK',
        GBP: '9 GBP',
        NOK: '0 NOK',
        EUR: '9 EUR',
    },
}

export const trackChoosePlanModalActioned = ({
    action,
    step,
    origin,
    section,
    plan,
    interval,
    commitment,
}: {
    action:
        | 'abandoned'
        | 'back'
        | 'completed'
        | 'get_started'
        | 'purchase_terms_link'
        | 'view_all_link'
        | 'viewed'
    step: 'payment_plan' | 'pricing_plan'
    origin: PageOrigin
    section?: PageSection
    plan?: AllowedPlanType
    interval?: 'MONTH' | 'YEAR'
    commitment?: 1 | 12
}) => {
    choosePlanModalActioned({
        action,
        step,
        origin,
        ...(section ? {section} : null),
        ...(plan ? {plan} : null),
        ...(interval ? {interval} : null),
        ...(commitment ? {commitment} : null),
    })
}

// We will have to simplify the contract payload to only include the necessary fields
// on a following ticket
export const getUpdateCompanySubscriptionParams = ({
    billingCommitment,
    planType,
    companyId,
    numberOfUsers,
    planUpgradeSource,
    purchaseAgreementUrl,
    planPrices,
}: {
    billingCommitment: BillingCommitment
    planType: AllowedPlanType
    companyId: string
    numberOfUsers: number
    planUpgradeSource: PlanUpgradeSource
    purchaseAgreementUrl: string
    planPrices?: PlanPrices
}): UpdateSubscriptionRequest => {
    return {
        companyId,
        planType,
        intervalCommitment: 1,
        // FLEXIBLE commitment charges are monthly, otherwise it's the same as the commitment type
        intervalType: billingCommitment === 'FLEXIBLE' ? 'MONTHLY' : billingCommitment,
        numberOfUsers,
        // cashback is only available for yearly plans
        includeCashback: billingCommitment === 'FLEXIBLE' ? false : true,
        // STARTER Plan doesn't have a clickwrap agreement
        clickwrapAgreement:
            planType === 'STARTER'
                ? undefined
                : {
                      contractPrice: {
                          currency: planPrices?.currency as AmountCurrency,
                          value:
                              billingCommitment === 'YEARLY'
                                  ? planPrices?.prices.yearly
                                  : planPrices?.prices.monthly,
                      },
                      discountedContractPrice: {
                          currency: planPrices?.currency as AmountCurrency,
                          value:
                              // Only YEARLY plans have discounted prices
                              billingCommitment === 'YEARLY'
                                  ? planPrices?.prices.yearlyDiscounted
                                  : planPrices?.prices.monthly,
                      },
                      purchaseAgreementUrl,
                      // Commitment contracts are anual, regardless of the billing interval
                      // FLEXIBLE commitment contracts are for 1 month
                      contractIntervalCommitment: billingCommitment === 'FLEXIBLE' ? 1 : 12,
                  },
        planUpgradeSource: planUpgradeSource as UpdateSubscriptionRequestPlanUpgradeSource,
    }
}

export const addPercentageToValue = ({value, percentage}: {value: number; percentage: number}) =>
    value * (percentage / 100) + value
